import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, Loader2, Sparkles, Share2, Eye, Code2, ExternalLink, RefreshCw, Smartphone, Rocket } from "lucide-react";
import { toast } from "sonner";
import i18n from "@/i18n";
import { scrollToSection } from "@/lib/scroll";
import { publishingApi } from "@/lib/publishingApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = [
  "A fitness tracker with daily streaks",
  "A budget planner for freelancers",
  "A recipe finder from fridge photos",
  "A habit tracker with reminders",
];

function cleanHtml(raw) {
  let s = raw.trim();
  s = s.replace(/^```(?:html)?\s*/i, "").replace(/```\s*$/i, "");
  const idx = s.toLowerCase().indexOf("<!doctype");
  if (idx > 0) s = s.slice(idx);
  return s;
}

export default function AppBuilder() {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]); // {role, content}
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("preview");
  const [provider, setProvider] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const sessionRef = useRef(null);
  const lastPromptRef = useRef("");
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/ai-status`).then((r) => r.json()).then(setProvider).catch(() => {});
  }, []);

  const build = useCallback(
    async (text) => {
      const prompt = (text ?? "").trim();
      if (!prompt || loading) return;
      lastPromptRef.current = prompt;
      const history = messages;
      setMessages((m) => [...m, { role: "user", content: prompt }]);
      setInput("");
      setLoading(true);
      setProgress(5);
      setTab("code");
      let acc = "";
      try {
        const res = await fetch(`${API}/build`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: prompt, session_id: sessionRef.current, history, lang: i18n.language }),
        });
        if (!res.ok || !res.body) throw new Error();
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop();
          for (const part of parts) {
            const line = part.replace(/^data:\s*/, "").trim();
            if (!line) continue;
            const data = JSON.parse(line);
            if (data.delta) {
              acc += data.delta;
              setHtml(acc);
              setProgress((p) => Math.min(95, p + 1.2));
            }
            if (data.session_id) sessionRef.current = data.session_id;
          }
        }
        const finalHtml = cleanHtml(acc);
        setHtml(finalHtml);
        setMessages((m) => [...m, { role: "assistant", content: finalHtml }]);
        setProgress(100);
        setTab("preview");
        toast.success("App built! Try it in the preview →");
      } catch {
        toast.error("Build failed. Please try again.");
      } finally {
        setLoading(false);
        setTimeout(() => setProgress(0), 800);
      }
    },
    [loading, messages]
  );

  useEffect(() => {
    const handler = (e) => {
      scrollToSection("#chat");
      setTimeout(() => build(e.detail), 600);
    };
    window.addEventListener("arca-build", handler);
    return () => window.removeEventListener("arca-build", handler);
  }, [build]);

  const shareApp = async () => {
    if (!html || sharing) return;
    setSharing(true);
    try {
      const res = await fetch(`${API}/blueprints`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: lastPromptRef.current, content: cleanHtml(html), kind: "app" }),
      });
      if (!res.ok) throw new Error();
      const { slug } = await res.json();
      const url = `${window.location.origin}/b/${slug}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Public app link copied!", { description: url });
      } catch {
        toast.success("App published!", { description: url });
      }
    } catch {
      toast.error("Couldn't publish app. Try again.");
    } finally {
      setSharing(false);
    }
  };

  const openApp = () => {
    const w = window.open();
    if (w) {
      w.document.open();
      w.document.write(cleanHtml(html));
      w.document.close();
    }
  };

  const publishApp = async () => {
    if (!html || publishing) return;
    setPublishing(true);
    try {
      const name = (lastPromptRef.current || "My App").replace(/^build (this app:|an? )?/i, "").trim().slice(0, 40) || "My App";
      const project = await publishingApi.createProject({ name, platform: "both", app_html: cleanHtml(html) });
      toast.success("Publishing project created");
      navigate(`/publish/${project.id}`);
    } catch (e) {
      toast.error(e.message || "Couldn't start publishing.");
    } finally {
      setPublishing(false);
    }
  };

  const hasApp = html && html.trim().length > 0;

  return (
    <section id="chat" className="py-28 md:py-36 relative overflow-hidden">
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-1/4 w-[40rem] h-[40rem] bg-[#0055FF]/10 blur-[130px] rounded-full" />
      <div className="max-w-7xl mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4">/ ai app builder</p>
          <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">Describe it. Watch it get built.</h2>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto">Type an idea and ArcaCentum generates a real, working app you can use live — then refine it in plain English.</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          {/* Left: prompt + refine */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6 flex flex-col"
            data-testid="builder-panel"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono text-xs uppercase tracking-widest text-slate-500">Prompt</span>
              {provider && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 border border-white/10 rounded-full px-2.5 py-1" data-testid="chat-provider-badge">
                  {provider.model}
                </span>
              )}
            </div>

            {messages.length > 0 && (
              <div className="mb-4 space-y-2 max-h-32 overflow-y-auto" data-testid="builder-history">
                {messages.filter((m) => m.role === "user").map((m, i) => (
                  <div key={i} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-[#00F0FF] font-mono text-xs mt-0.5">{i === 0 ? "build" : "edit"}</span>
                    <span>{m.content}</span>
                  </div>
                ))}
              </div>
            )}

            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  build(input);
                }
              }}
              placeholder={hasApp ? "Refine it… e.g. add a dark mode toggle" : "Build a fitness tracker with daily streaks…"}
              rows={hasApp ? 2 : 4}
              className="w-full resize-none bg-black/40 border border-white/10 rounded-2xl px-5 py-4 outline-none text-white placeholder:text-slate-600 font-mono text-sm focus:border-[#0055FF] transition-colors duration-300"
              data-testid="chat-input"
            />

            <button
              onClick={() => build(input)}
              disabled={loading || !input.trim()}
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white px-6 py-4 rounded-2xl font-semibold shadow-[0_0_30px_rgba(0,85,255,0.45)] disabled:opacity-40 disabled:shadow-none hover:-translate-y-0.5 transition-transform duration-300"
              data-testid="chat-generate-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
              {loading ? "Building your app…" : hasApp ? "Apply change" : "Generate app"}
            </button>

            <div className="flex flex-wrap gap-2 mt-5">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => build(s)}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 text-xs font-mono text-slate-400 border border-white/10 rounded-full px-3 py-2 hover:border-[#00F0FF]/40 hover:text-white transition-colors duration-300 disabled:opacity-40"
                  data-testid={`chat-suggestion-${i}`}
                >
                  <Sparkles size={11} className="text-[#00F0FF]" /> {s}
                </button>
              ))}
            </div>

            {progress > 0 && (
              <div className="mt-5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#0055FF] to-[#00F0FF] transition-[width] duration-200" style={{ width: `${progress}%` }} />
              </div>
            )}
          </motion.div>

          {/* Right: preview / code */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="rounded-3xl tracing-beam"
          >
            <div className="rounded-3xl bg-[#0a0a0a] border border-white/10 overflow-hidden h-full flex flex-col">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-black/40">
                <button
                  onClick={() => setTab("preview")}
                  className={`inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-lg transition-colors duration-300 ${tab === "preview" ? "bg-[#0055FF] text-white" : "text-slate-400 hover:text-white"}`}
                  data-testid="app-tab-preview"
                >
                  <Eye size={13} /> Preview
                </button>
                <button
                  onClick={() => setTab("code")}
                  className={`inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-lg transition-colors duration-300 ${tab === "code" ? "bg-[#0055FF] text-white" : "text-slate-400 hover:text-white"}`}
                  data-testid="app-tab-code"
                >
                  <Code2 size={13} /> Code
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={openApp} disabled={!hasApp} className="text-slate-400 hover:text-white disabled:opacity-30 transition-colors duration-300 p-1.5" title="Open in new tab" data-testid="app-open-btn">
                    <ExternalLink size={15} />
                  </button>
                  <button onClick={shareApp} disabled={!hasApp || sharing} className="inline-flex items-center gap-1.5 text-xs font-mono bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg disabled:opacity-30 transition-colors duration-300" data-testid="app-share-btn">
                    {sharing ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />} Share
                  </button>
                </div>
              </div>

              <div className="flex-1 min-h-[520px] bg-[#050505] relative">
                {!hasApp && !loading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8" data-testid="app-empty">
                    <Smartphone size={40} className="text-slate-700 mb-4" />
                    <p className="text-slate-500 font-mono text-sm">Your working app will appear here.</p>
                    <p className="text-slate-600 text-xs mt-2">Describe an idea or tap a suggestion to start.</p>
                  </div>
                )}

                {tab === "preview" && hasApp && (
                  <div className="h-full flex items-center justify-center p-6">
                    <div className="w-full max-w-[400px] h-[560px] rounded-[2rem] border-4 border-zinc-800 overflow-hidden shadow-2xl bg-black">
                      <iframe
                        title="app-preview"
                        srcDoc={cleanHtml(html)}
                        className="w-full h-full"
                        sandbox="allow-scripts allow-forms allow-modals allow-popups allow-same-origin"
                        data-testid="app-preview-iframe"
                      />
                    </div>
                  </div>
                )}

                {tab === "code" && (
                  <pre className="absolute inset-0 overflow-auto p-5 font-mono text-[11px] leading-relaxed text-slate-300" data-testid="app-code">
                    {html || "// generating code…"}
                    {loading && <span className="inline-block w-2 h-3 bg-[#00F0FF] ml-0.5 animate-pulse align-middle" />}
                  </pre>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {hasApp && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 rounded-3xl border border-[#0055FF]/30 bg-gradient-to-br from-[#0055FF]/10 to-transparent p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5"
            data-testid="publish-cta"
          >
            <div>
              <h3 className="font-display font-bold text-2xl mb-1 flex items-center gap-2"><Rocket size={20} className="text-[#00F0FF]" /> Publish your app</h3>
              <p className="text-slate-400 text-sm max-w-lg">Package, validate and submit to the App Store &amp; Google Play — without leaving ArcaCentum. Apple &amp; Google still review every submission.</p>
            </div>
            <button
              onClick={publishApp}
              disabled={publishing}
              className="inline-flex items-center gap-2 bg-white text-black px-7 py-4 rounded-2xl font-semibold shrink-0 hover:bg-[#00F0FF] disabled:opacity-40 transition-colors duration-300"
              data-testid="publish-your-app-btn"
            >
              {publishing ? <Loader2 size={18} className="animate-spin" /> : <Rocket size={18} />} Publish your app
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
}
